import {
	ACCESS_RESOURCES,
	FEATURE_PLAN_MAP,
	type FeatureKey,
	type PlanId,
	type ResourceAccessConfig,
	type ResourceId,
} from '@/config/access-control'
import { PLAN_HIERARCHY } from '@/config/plans'
import type { Permissions } from '@/rbac/grants'
import { PERMISSIONS } from '@/rbac/permissions'
import {
	ActivityIcon,
	BarChart3,
	BookUser,
	Boxes,
	ChartColumn,
	CreditCard,
	Database,
	FileInput,
	FileTextIcon,
	Key,
	type LucideIcon,
	Radio,
	Send,
	Settings,
	ShieldUser,
	SlidersVertical,
	Tag,
	Users,
	Wrench,
} from 'lucide-react'
import React from 'react'

export interface NavItem {
	id: string
	title: string
	path: string
	icon?: LucideIcon
	isActive?: boolean
	items?: NavItem[]
	hasSettings?: boolean
	settingsOptions?: NavSettingsOption[]
	isVisible?: boolean
	metadata?: Record<string, unknown>
	external?: boolean
	badge?: {
		text: string
		variant?: 'default' | 'secondary' | 'destructive' | 'outline'
	}
	/* Key used to map this nav item to show_tabs settings for module visibility */
	moduleKey?: string
	resource?: ResourceId
	featureKey?: FeatureKey
	permissionKey?: keyof Permissions
	/** Hide from non-admin users (mirrors the per-route projectSettingsAdminRouteGuard). */
	adminOnly?: boolean
}

export interface NavSettingsOption {
	id: string
	label: string
	path: string
	icon?: LucideIcon
	items?: {
		id: string
		title: string
		path: string
	}[]
	/** Same shape as NavItem — opt-in registry-driven access control. */
	resource?: ResourceId
	featureKey?: FeatureKey
	permissionKey?: keyof Permissions
}

export interface Tenant {
	id: string
	name: string
	isActive?: boolean
	metadata?: Record<string, unknown>
}

const HardDriveIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		width='24'
		height='24'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		className='w-5 h-5'
		ref={ref}
		{...props}>
		<title>Hard Drive Icon</title>
		<line x1='22' x2='2' y1='12' y2='12' />
		<path d='M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z' />
		<line x1='6' x2='6.01' y1='16' y2='16' />
		<line x1='10' x2='10.01' y1='16' y2='16' />
	</svg>
))

export const navData: NavItem[] = [
	{
		id: 'device-management',
		title: 'Device Management',
		path: '/device-management/all',
		icon: HardDriveIcon,
		isActive: true,
		moduleKey: 'device_management',
		resource: 'devices',
		hasSettings: true,
		settingsOptions: [
			{
				id: 'device-metadata-settings',
				label: 'Metadata',
				path: '/settings/metadata/all',
				icon: Tag,
				resource: 'metadata_keys',
			},
		],
		items: [
			{
				id: 'devices-all',
				title: 'All',
				path: '/device-management/all',
			},
		],
	},
	{
		id: 'data',
		title: 'Data',
		path: '/data',
		icon: Database,
		hasSettings: true,
		settingsOptions: [
			{
				id: 'streams-settings',
				label: 'Streams',
				path: '/settings/streams/all',
				icon: Database,
				resource: 'streams',
			},
		],
		items: [
			{
				id: 'dashboards',
				title: 'Dashboards',
				path: '/data/dashboards/all',
				icon: ChartColumn,
				moduleKey: 'dashboards',
				resource: 'dashboards',
				items: [
					{
						id: 'dashboards-all',
						title: 'All',
						path: '/data/dashboards/all',
					},
					{
						id: 'dashboards-device',
						title: 'Device',
						path: '/data/dashboards/device',
					},
					{
						id: 'dashboards-fleet',
						title: 'Fleet',
						path: '/data/dashboards/fleet',
					},
				],
			},
			{
				id: 'dbc-parsers',
				title: 'DBC Parsers',
				path: '/data/dbc-parsers/all',
				icon: FileInput,
				moduleKey: 'dbc_parsers',
				resource: 'dbc_parsers',
				items: [
					{
						id: 'dbc-all',
						title: 'All',
						path: '/data/dbc-parsers/all',
					},
					{
						id: 'dbc-custom',
						title: 'Custom',
						path: '/data/dbc-parsers/custom',
					},
					{
						id: 'dbc-obd',
						title: 'OBD',
						path: '/data/dbc-parsers/obd',
					},
					{
						id: 'dbc-j1939',
						title: 'J1939',
						path: '/data/dbc-parsers/j1939',
					},
				],
			},
			{
				id: 'exporters',
				title: 'Exporters',
				path: '/data/exporters/all',
				icon: Send,
				moduleKey: 'exporters',
				resource: 'exporters',
				items: [
					{
						id: 'exporters-all',
						title: 'All',
						path: '/data/exporters/all',
					},
					{
						id: 'exporters-http',
						title: 'HTTP',
						path: '/data/exporters/http',
					},
				],
			},
		],
	},
	{
		id: 'actions',
		title: 'Actions',
		path: '/actions',
		icon: ActivityIcon,
		hasSettings: true,
		settingsOptions: [
			{
				id: 'action-settings',
				label: 'Action Settings',
				path: '/settings/action-settings/active',
				icon: Settings,
				resource: 'custom_commands',
			},
		],
		items: [
			{
				id: 'remote-commands',
				title: 'Remote Commands',
				path: '/actions/remote-commands/all',
				icon: ActivityIcon,
				moduleKey: 'remote_commands',
				resource: 'remote_commands',
			},
			// OTA Campaigns nav entry hidden — not ready for release. Route/page code left intact.
			// {
			// 	id: 'ota-campaigns',
			// 	title: 'OTA Campaigns',
			// 	path: '/actions/ota-campaigns/all',
			// 	icon: Send,
			// 	moduleKey: 'ota_campaigns',
			// 	resource: 'ota_campaigns',
			// },
		],
	},
	{
		id: 'sessions',
		title: 'Sessions',
		path: '/sessions/dashboard',
		icon: Radio,
		hasSettings: true,
		permissionKey: PERMISSIONS.SESSION_RULE_READ,
		moduleKey: 'sessions',
		settingsOptions: [
			{
				id: 'subscriber-groups',
				label: 'Subscriber Groups',
				path: '/settings/subscriber-groups/all',
				icon: BookUser,
				resource: 'subscriber_groups',
				items: [
					{
						id: 'all',
						title: 'All',
						path: '/settings/subscriber-groups/all',
					},
					{
						id: 'email',
						title: 'Email',
						path: '/settings/subscriber-groups/email',
					},
					{
						id: 'slack',
						title: 'Slack',
						path: '/settings/subscriber-groups/slack',
					},
					{
						id: 'webhook',
						title: 'Webhook',
						path: '/settings/subscriber-groups/webhook',
					},
				],
			},
		],
	},
]

export const organizationNavData: NavItem[] = [
	{
		id: 'org-inventory',
		title: 'Inventory',
		path: '/inventory/all',
		icon: Boxes,
		resource: 'devices',
		hasSettings: true,
		settingsOptions: [
			{
				id: 'inventory-metadata-settings',
				label: 'Metadata',
				path: '/settings/metadata/all',
				icon: Tag,
				resource: 'metadata_keys',
			},
		],
		items: [
			{
				id: 'inventory-all',
				title: 'All',
				path: '/inventory/all',
			},
		],
	},
	{
		id: 'org-meters',
		title: 'Meters',
		path: '/meters',
		icon: BarChart3,
	},
	{
		id: 'org-operations',
		title: 'Operations',
		path: '/operations',
		icon: Wrench,
		resource: 'remote_commands',
		items: [
			{
				id: 'operations-all',
				title: 'All',
				path: '/operations/all',
			},
		],
	},
	{
		id: 'org-billing',
		title: 'Billing',
		path: '/billing/plan-details',
		icon: CreditCard,
	},
]

export const organizationSettingsNavData: NavItem[] = [
	{
		id: 'org-general-settings',
		title: 'General',
		path: '/settings/general',
		icon: SlidersVertical,
	},
	{
		id: 'org-metadata-settings',
		title: 'Organization Metadata',
		path: '/settings/metadata/all',
		icon: Tag,
		resource: 'metadata_keys',
	},
	{
		id: 'org-streams-settings',
		title: 'Organization Streams',
		path: '/settings/streams/all',
		icon: Database,
		resource: 'streams',
	},
	{
		id: 'org-users-settings',
		title: 'Users',
		path: '/settings/users/all',
		icon: Users,
		resource: 'users',
		items: [
			{
				id: 'org-all-users',
				title: 'All',
				path: '/settings/users/all',
			},
		],
	},
	{
		id: 'org-api-keys-settings',
		title: 'API Keys',
		path: '/settings/api-keys',
		icon: Key,
		resource: 'api_keys',
		items: [
			{
				id: 'org-all-api-keys',
				title: 'All',
				path: '/settings/api-keys/all',
			},
		],
	},
	{
		id: 'org-audit-logs-settings',
		title: 'Audit Logs',
		path: '/settings/audit-logs/all',
		icon: FileTextIcon,
	},
]

export const settingsNavData: NavItem[] = [
	{
		id: 'general-settings',
		title: 'General',
		path: '/settings/general',
		icon: SlidersVertical,
		adminOnly: true,
	},
	{
		id: 'metadata-settings',
		title: 'Metadata',
		path: '/settings/metadata/all',
		icon: Tag,
		resource: 'metadata_keys',
		items: [
			{
				id: 'metadata-all',
				title: 'All',
				path: '/settings/metadata/all',
			},
		],
	},
	{
		id: 'streams-settings',
		title: 'Streams',
		path: '/settings/streams/all',
		icon: Database,
		resource: 'streams',
		items: [
			{
				id: 'streams-all',
				title: 'All',
				path: '/settings/streams/all',
			},
		],
	},
	{
		id: 'action-settings',
		title: 'Action Settings',
		path: '/settings/action-settings/active',
		icon: Settings,
		resource: 'custom_commands',
		items: [
			{
				id: 'action-settings-active',
				title: 'Active',
				path: '/settings/action-settings/active',
			},
			{
				id: 'action-settings-inactive',
				title: 'Inactive',
				path: '/settings/action-settings/inactive',
			},
		],
	},
	{
		id: 'subscriber-groups-settings',
		title: 'Subscriber Groups',
		path: '/settings/subscriber-groups/all',
		icon: BookUser,
		resource: 'subscriber_groups',
		items: [
			{
				id: 'subscriber-groups-all',
				title: 'All',
				path: '/settings/subscriber-groups/all',
			},
			{
				id: 'subscriber-groups-email',
				title: 'Email',
				path: '/settings/subscriber-groups/email',
			},
			{
				id: 'subscriber-groups-slack',
				title: 'Slack',
				path: '/settings/subscriber-groups/slack',
			},
			{
				id: 'subscriber-groups-webhook',
				title: 'Webhook',
				path: '/settings/subscriber-groups/webhook',
			},
		],
	},
	{
		id: 'users-settings',
		title: 'Users',
		path: '/settings/users/all',
		icon: Users,
		resource: 'users',
		adminOnly: true,
		items: [
			{
				id: 'all-users',
				title: 'All',
				path: '/settings/users/all',
			},
			{
				id: 'admins',
				title: 'Admins',
				path: '/settings/users/admin',
			},
		],
	},
	{
		id: 'api-keys-settings',
		title: 'API Keys',
		path: '/settings/api-keys',
		icon: Key,
		resource: 'api_keys',
		adminOnly: true,
		items: [
			{
				id: 'all-api-keys',
				title: 'All',
				path: '/settings/api-keys/all',
			},
		],
	},
	{
		id: 'roles-settings',
		title: 'Roles',
		path: '/settings/roles',
		icon: ShieldUser,
		resource: 'roles',
		adminOnly: true,
		items: [
			{
				id: 'all-roles',
				title: 'All',
				path: '/settings/roles/all',
			},
		],
	},
	// Quotas nav entry hidden — not ready for release. Route/page code left intact.
	// {
	// 	id: 'quotas-settings',
	// 	title: 'Quotas',
	// 	path: '/settings/quotas',
	// 	icon: BarChart3,
	// 	adminOnly: true,
	// },
	{
		id: 'audit-logs-settings',
		title: 'Audit Logs',
		path: '/settings/audit-logs/all',
		icon: FileTextIcon,
		moduleKey: 'audit_logs',
		adminOnly: true,
		items: [
			{
				id: 'audit-logs-all',
				title: 'All',
				path: '/settings/audit-logs/all',
			},
		],
	},
	// API Diagnostics nav entry hidden — not ready for release. Route/page code left intact.
	// {
	// 	id: 'diagnostics-settings',
	// 	title: 'API Diagnostics',
	// 	path: '/settings/diagnostics',
	// 	icon: ActivityIcon,
	// },
]

export const findNavItemById = (items: NavItem[], id: string): NavItem | null => {
	for (const item of items) {
		if (item.id === id) {
			return item
		}
		if (item.items) {
			const found = findNavItemById(item.items, id)
			if (found) {
				return found
			}
		}
	}
	return null
}

export const findNavItemByPath = (items: NavItem[], path: string): NavItem | null => {
	for (const item of items) {
		if (item.path === path) {
			return item
		}
		if (item.items) {
			const found = findNavItemByPath(item.items, path)
			if (found) {
				return found
			}
		}
	}
	return null
}

export type ShowTabsData = Record<string, { show: boolean }>

/**
 * Filters navigation items based on module visibility settings from show_tabs.
 * If a module is disabled (show: false), the corresponding nav item will be hidden.
 * For parent items like "Data" and "Actions", if all child items are disabled,
 * the parent will also be hidden.
 *
 * @param items - The navigation items to filter
 * @param showTabs - The show_tabs settings from tenant settings (null means all modules are visible)
 * @returns Filtered navigation items with disabled modules removed
 */
export const filterNavItemsByModules = (items: NavItem[], showTabs: ShowTabsData | null | undefined): NavItem[] => {
	if (!showTabs) {
		return items
	}

	return items
		.map(item => {
			const filteredChildItems = item.items ? filterNavItemsByModules(item.items, showTabs) : undefined

			return {
				...item,
				items: filteredChildItems,
			}
		})
		.filter(item => {
			if (item.moduleKey) {
				const moduleData = showTabs[item.moduleKey]
				if (moduleData && moduleData.show === false) {
					return false
				}
			}

			// Hide empty parents that don't declare their own moduleKey — they've lost all their children.
			if (item.items !== undefined && item.items.length === 0 && !item.moduleKey) {
				return false
			}

			return true
		})
}

export interface FilteredNavItem extends NavItem {
	/** When set, the nav item should render but be marked as locked (e.g. plan-gated). */
	locked?: { reason: 'plan-locked'; requiredPlan: PlanId }
	items?: FilteredNavItem[]
	settingsOptions?: NavSettingsOption[]
}

export interface FilterNavContext {
	currentPlan: PlanId | null
	showTabs?: ShowTabsData | null
}

/**
 * Unified filter combining plan-feature and show_tabs gating.
 *
 * Rules:
 * - Plan-lock  → item is kept and marked `locked` so the sidebar can show a lock badge
 * - show_tabs  → org-level module visibility; omitted
 * - Empty parents after child filtering are pruned unless they declare their own moduleKey.
 *
 * RBAC is not applied in the sidebar; module pages gate access inline via RouteAccessBoundary.
 */
export function filterNavItems(items: NavItem[], ctx: FilterNavContext): FilteredNavItem[] {
	const { currentPlan, showTabs } = ctx

	const resolveFeatureKey = (item: NavItem): FeatureKey | undefined => {
		if (item.featureKey) {
			return item.featureKey
		}
		if (item.resource) {
			return (ACCESS_RESOURCES[item.resource] as ResourceAccessConfig).featureKey
		}
		return undefined
	}

	const planSatisfies = (required: FeatureKey): boolean => {
		if (currentPlan == null) {
			return false
		}
		const requiredPlan = FEATURE_PLAN_MAP[required]
		const currentIdx = PLAN_HIERARCHY.indexOf(currentPlan as (typeof PLAN_HIERARCHY)[number])
		const requiredIdx = PLAN_HIERARCHY.indexOf(requiredPlan as (typeof PLAN_HIERARCHY)[number])
		if (currentIdx === -1 || requiredIdx === -1) {
			console.warn(
				'[filterNavItems] planSatisfies: current or required plan missing from PLAN_HIERARCHY (fail closed).',
				{ currentPlan, featureKey: required, requiredPlan },
			)
			return false
		}
		return currentIdx >= requiredIdx
	}

	const filterSettingsOptions = (options: NavSettingsOption[] | undefined): NavSettingsOption[] | undefined => {
		if (!options) {
			return undefined
		}
		return options.filter(option => {
			const featureKey = option.featureKey
			if (featureKey && !planSatisfies(featureKey)) {
				return false
			}
			return true
		})
	}

	const visit = (list: NavItem[]): FilteredNavItem[] =>
		list
			.map(item => {
				const childItems = item.items ? visit(item.items) : undefined

				// show_tabs module visibility (org-level switch)
				if (showTabs && item.moduleKey) {
					const moduleData = showTabs[item.moduleKey]
					if (moduleData && moduleData.show === false) {
						return null
					}
				}

				// Plan-lock check — keep the item and mark it locked (discoverability)
				const featureKey = resolveFeatureKey(item)
				const locked =
					featureKey && !planSatisfies(featureKey)
						? { reason: 'plan-locked' as const, requiredPlan: FEATURE_PLAN_MAP[featureKey] as PlanId }
						: undefined

				const result: FilteredNavItem = {
					...item,
					items: childItems,
					settingsOptions: filterSettingsOptions(item.settingsOptions),
					locked,
				}
				return result
			})
			.filter((item): item is FilteredNavItem => item !== null)
			.filter(item => {
				// Prune empty parents unless they have their own moduleKey (leaf parents)
				if (item.items !== undefined && item.items.length === 0 && !item.moduleKey && !item.resource) {
					return false
				}
				return true
			})

	return visit(items)
}

/**
 * A nav entry's `path` names the tab its module opens on, not the module. These two read the convention
 * back out, and both consumers of it share them: the sidebar resource lookup below, and the page header
 * deciding which tabs a URL shows. Kept together so the segment list cannot drift between them.
 */
const DEFAULT_TAB_SEGMENT = /\/(all|active|inactive)$/

/** The module a nav path belongs to, without the tab it happens to open on. */
export function stripDefaultTabSegment(path: string): string {
	return path.replace(DEFAULT_TAB_SEGMENT, '')
}

/** True for the module itself and anything under it, but never for a sibling that shares a name prefix. */
export function isUnderBasePath(path: string, basePath: string): boolean {
	return path === basePath || path.startsWith(`${basePath}/`)
}

/**
 * Modules the scan below cannot reach, because they have no nav entry carrying their resource.
 * Sessions is one entry for both the feed (`/sessions/dashboard`) and its rules (`/sessions/rules`).
 */
const RESOURCE_BY_ROUTE_PREFIX: ReadonlyArray<readonly [string, string]> = [['/sessions', 'session_rules']]

/**
 * RBAC resource that governs a normalized route path, e.g. `/data/exporters/all` -> `exporters`.
 *
 * Children are matched before their parent: a group like Data owns the `/data` prefix of every module
 * under it but carries no resource itself, so matching the parent would resolve them all to nothing.
 */
export function getResourceForPath(path: string): string | undefined {
	for (const [prefix, resource] of RESOURCE_BY_ROUTE_PREFIX) {
		if (path.startsWith(prefix)) {
			return resource
		}
	}
	const allNavItems = [...settingsNavData, ...organizationSettingsNavData, ...navData]
	const matchesPath = (navPath: string) => isUnderBasePath(path, stripDefaultTabSegment(navPath))
	for (const item of allNavItems) {
		if (item.items) {
			for (const child of item.items) {
				if (child.path && matchesPath(child.path)) {
					return child.resource || item.resource
				}
			}
		}
		if (item.path && matchesPath(item.path)) {
			return item.resource
		}
	}
	return undefined
}
